import { useState, useEffect, useMemo } from "react";

/**
 * Detects if the device is a touch device (mobile/tablet)
 * Uses multiple detection methods for reliability:
 * - navigator.maxTouchPoints: Modern way to detect touch capability
 * - matchMedia('(hover: none)'): Detects if device doesn't support hover
 * - matchMedia('(pointer: coarse)'): Detects coarse pointer (finger vs mouse)
 */
const getIsTouchDevice = (): boolean => {
  if (typeof window === "undefined") return false;

  const hasTouchPoints = navigator.maxTouchPoints > 0;
  const hasNoHover = window.matchMedia("(hover: none)").matches;
  const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;

  return hasTouchPoints || hasNoHover || hasCoarsePointer;
};

export const useResponsive = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [isTouchDevice, setIsTouchDevice] = useState(getIsTouchDevice);

  const handleResize = () => {
    setWindowSize(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    // Listen for changes in hover/pointer media queries (e.g., when connecting a mouse to tablet)
    const hoverQuery = window.matchMedia("(hover: none)");
    const pointerQuery = window.matchMedia("(pointer: coarse)");

    const handleMediaChange = () => {
      setIsTouchDevice(getIsTouchDevice());
    };

    hoverQuery.addEventListener("change", handleMediaChange);
    pointerQuery.addEventListener("change", handleMediaChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      hoverQuery.removeEventListener("change", handleMediaChange);
      pointerQuery.removeEventListener("change", handleMediaChange);
    };
  }, []);

  const isMobile = windowSize <= 480;
  const isTablet = windowSize > 480 && windowSize <= 1024;
  const isDesktop = windowSize > 1024;

  // Device should use mobile UI for:
  // - Mobile screens (≤480px)
  // - Tablet screens (481-1024px)
  // - Touch devices that are tablet-sized or smaller
  // But NOT for desktop-sized touch devices (touchscreen laptops)
  const shouldUseMobileUI =
    isMobile || isTablet || (isTouchDevice && !isDesktop);

  const responsive = (width: number, type: "min" | "max") => {
    if (type === "min") {
      return windowSize >= width;
    }
    if (type === "max") {
      return windowSize <= width;
    }
  };

  return {
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    shouldUseMobileUI,
    windowSize,
    responsive,
  };
};
